import { useCallback, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { Thread } from "@/models/agent/thread";
import { ThreadsResponse } from "@/services/agent/agent.api.types";
import AgentEndpoints from "@/services/agent/agent.endpoint";
import { AgentService } from "@/services/agent/agent.service";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import { useAgentStore } from "@/stores/agent/useAgentStore";

export const PAGE_SIZE = 20;

interface UseInfiniteThreadsResult {
  threads: Thread[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: unknown;
  hasMore: boolean;
  refresh: () => Promise<void>;
  observerRef: (node: HTMLDivElement | null) => void;
}

export const useInfiniteThreads = (): UseInfiniteThreadsResult => {
  const { sessionCurrentWorkspaceId } = useWorkspaces();

  const threads = useAgentStore(state => state.threads);
  const addThreads = useAgentStore(state => state.addThreads);
  const resetThreads = useAgentStore(state => state.resetThreads);

  const [offset, setOffset] = useState(threads.length);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 初回ロードの状態
  const fetcher = async () => {
    if (!sessionCurrentWorkspaceId) return [];
    return await AgentService.getThreads(sessionCurrentWorkspaceId, 0, PAGE_SIZE);
  };

  const { data, error, isLoading, mutate } = useSWR(
    sessionCurrentWorkspaceId ? ["agent", "threads", sessionCurrentWorkspaceId, 0, PAGE_SIZE] : null,
    fetcher,
    defaultSWROption,
  );

  // Intersection Observer用のref
  const observer = useRef<IntersectionObserver>();

  useEffect(() => {
    // ワークスペース切り替え時に状態をリセット
    resetThreads();
    setOffset(0);
    setHasMore(true);
  }, [sessionCurrentWorkspaceId, resetThreads]);

  // 初回データの処理
  useEffect(() => {
    if (isLoading) return;

    // 初回じゃない場合はスキップ
    if (threads.length > 0) return;

    if (data?.length) {
      addThreads(data);
      setOffset(data.length);
      // 返された件数がPAGE_SIZEより少ない場合は終了
      setHasMore(data.length === PAGE_SIZE);
    } else {
      // Threadsが0件の場合
      setHasMore(false);
    }
  }, [data, isLoading, threads.length, addThreads, sessionCurrentWorkspaceId]);

  // 追加データをロードする関数
  const loadMoreData = useCallback(async () => {
    if (!sessionCurrentWorkspaceId || isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    await new Promise(resolve => setTimeout(resolve, 1000));

    try {
      const url = AgentEndpoints.GET_THREADS(offset, PAGE_SIZE, sessionCurrentWorkspaceId);
      const response = await ApiService.fetchData<undefined, ThreadsResponse>({
        url,
        method: "GET",
      });

      const newThreads = response.data.threads.map(threadSchema =>
        new Thread(
          threadSchema.id,
          threadSchema.workspace_id,
          threadSchema.title,
          new Date(threadSchema.created_at),
          new Date(threadSchema.updated_at),
        )
      );

      addThreads(newThreads);
      setOffset(prev => prev + newThreads.length);
      // 返された件数がPAGE_SIZEより少ない場合は終了
      setHasMore(newThreads.length === PAGE_SIZE);
    } catch (err) {
      console.error("Error loading more threads:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [sessionCurrentWorkspaceId, offset, isLoadingMore, hasMore, addThreads]);

  // Intersection Observerのコールバック参照
  const observerRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoadingMore) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
        loadMoreData();
      }
    }, {
      threshold: 0.1,
      rootMargin: "400px", // 400px前から読み込み開始（より早く）
    });

    if (node) {
      observer.current.observe(node);
    }
  }, [isLoadingMore, hasMore, loadMoreData]);

  // データの再読み込み
  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return {
    threads,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    refresh,
    observerRef,
  };
};
